import {z} from "zod/v4-mini"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {mapFields} from "@cm/utils"
import {curveGeometryAttributeSetLikeMarker, GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {curveLike} from "#template/nodes/object/curve/fundamental"
import type {CurveLikeNode} from "#template/nodes/object/curve/fundamental"

import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"

import type {CurveGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {createCurveGeometryAttributeData, createCurveGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {GeomBuilderContext} from "#template/geometry-processing/geometry-graph"
import {continuousCurveAttr, Primitives, curveToGeom} from "#template/geometry-processing/geometry-graph"
import {geometryAttributeRefDataSetFromAttributeRefSets} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import type {AbstractCurveData, ReifiedCurveData} from "#template/interfaces/curve-data"

export type CurveGeometryAttributeSetCurveLikeInputParameters = {
    curve: CurveLikeNode | null
}

const definition: TemplateNodeDefinition<CurveGeometryAttributeSetCurveLikeInputParameters> = {
    nodeClass: "CurveGeometryAttributeSetCurveLikeInput",
    validation: {
        parameters: z.object({
            curve: z.nullable(curveLike),
        }),
        returns: getTemplateNodeReturns(),
    },
}

function curveGeometryAttributeRefDataSetFromReifiedCurveData(reifiedCurveData: Omit<ReifiedCurveData, "contentHash">, ctx: GeomBuilderContext) {
    const reifiedCurveRefSet = Primitives.curve(ctx, {
        points: continuousCurveAttr(reifiedCurveData.points, "float3"),
        normals: continuousCurveAttr(reifiedCurveData.normals, "float3"),
        tangents: continuousCurveAttr(reifiedCurveData.tangents, "float3"),
        segments: continuousCurveAttr(reifiedCurveData.segments, "float"),
        scales: continuousCurveAttr(reifiedCurveData.scales, "float"),
        sectionIds: continuousCurveAttr(reifiedCurveData.sectionIds, "uint32"),
    })

    return geometryAttributeRefDataSetFromAttributeRefSets(reifiedCurveRefSet, reifiedCurveRefSet, {})
}

function curveGeometryAttributeRefDataSetFromAbstractCurveData(abstractCurveData: Omit<AbstractCurveData, "contentHash">, ctx: GeomBuilderContext) {
    const displayAttrRefSet = curveToGeom(ctx, abstractCurveData.displayGeometryGraph)
    const renderAttrRefSet = curveToGeom(ctx, abstractCurveData.renderGeometryGraph)
    return geometryAttributeRefDataSetFromAttributeRefSets(displayAttrRefSet, renderAttrRefSet, abstractCurveData.displayGeometryGraphResources)
}

@registerTemplateGraphNode
export class CurveGeometryAttributeSetCurveLikeInput
    extends GeometryAttributeSetNode<1, CurveGeometryAttributeSetCurveLikeInputParameters>
    implements CurveGeometryAttributeSetLikeNode
{
    get [curveGeometryAttributeSetLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override hasValidParameters(
        parameters: CurveGeometryAttributeSetCurveLikeInputParameters,
    ): parameters is CurveGeometryAttributeSetCurveLikeInputParameters & NonNullableParameters<CurveGeometryAttributeSetCurveLikeInputParameters> {
        return parameters.curve !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<CurveGeometryAttributeSetCurveLikeInputParameters>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<1> | null>; invalidator?: BuilderInlet<null>} {
        const [attributeSetDataValid, attributeSetDataInvalid] = scope.branch(
            scope.pureLambda(
                scope.tuple(evaluator.evaluate(parameters.curve), ctx),
                ([evaluatedCurveData, ctx]) => {
                    const curveGeometryAttributeRefDataSet = (() => {
                        if (!evaluatedCurveData) return null
                        if (evaluatedCurveData.completeCurveData.abstract)
                            return curveGeometryAttributeRefDataSetFromAbstractCurveData(evaluatedCurveData.completeCurveData.abstract, ctx)
                        else if (evaluatedCurveData.completeCurveData.reified)
                            return curveGeometryAttributeRefDataSetFromReifiedCurveData(evaluatedCurveData.completeCurveData.reified, ctx)
                        else return null
                    })()

                    if (!curveGeometryAttributeRefDataSet) return null
                    return createCurveGeometryAttributeSetData(
                        mapFields(curveGeometryAttributeRefDataSet, (attrRefData) => createCurveGeometryAttributeData(attrRefData)),
                    )
                },
                "setupCurveAttributeSetData",
            ),
        )
        return {evaluatedGeometryData: attributeSetDataValid, invalidator: attributeSetDataInvalid}
    }
}
